import React from "react"
import {Grid, ClickAwayListener} from "@mui/material"
import {UxCTA as CTA} from "@next/ux-cta"
import {formatTextTestIds} from "@next/utils"
import {breakpoints} from "@next/ux-fabric"
import {useMediaQuery} from "../../../../hooks/useMediaQuery"
import urls from "../../../../config/urls"
import CountrySelectDropdown from "../../../CountrySelectDropdown"
import CountrySelectLanguageDropdown from "../../../CountrySelectLanguageDropdown"
import {CountryLanguage} from "../../../../models/countryselector"
import {COUNTRY_LANG_SELECTOR_DATA_GA} from "../../../../config/constants"
import {
    CountrySelectorContainer,
    InnerContainer,
    Title,
    CountrySelectorCTAWrapper,
    LanguageButtonsContainer,
    CloseButton,
    LanguageButton,
    CountrySelectorContainerElement,
    CountrySelectorROW,
    CountrySelectorROWText,
    ViewAllLink,
    DrawerContainer,
    StyledGrid,
} from "./component"
import type {DefaultTemplatePropType} from "../../index"
import {getAnchorDirection} from "../../util"

const TabCountryLangSelector = ({
    selectedCountry,
    selectedLanguage,
    selectLanguage,
    shopNowText,
    onCTAClick,
    headerTitle,
    closePanel,
    ROWText,
    ROWLinkText,
    ROWLinkUrl,
    isOpeningFromBottom,
    isOpenCountrySelectorDrawer,
    keepMounted,
    disablePortal,
    hideBackdrop,
    disableScrollLock,
    isShowOverlay,
    territory,
    textAlignment,
}: Required<DefaultTemplatePropType>) => {
    const showLanguageSelector = (selectedCountry?.Languages?.length ?? 0) > 1
    const isMedUp = useMediaQuery(`(min-width:${breakpoints.values.md.toString()}px)`)

    return (
        <DrawerContainer
            data-testid={formatTextTestIds(`header-drawer-country-selector`)}
            anchor={getAnchorDirection(isOpeningFromBottom)}
            open={isOpenCountrySelectorDrawer}
            onClose={closePanel}
            ModalProps={{keepMounted, disablePortal, hideBackdrop, disableScrollLock}}
            style={{position: isShowOverlay ? "fixed" : "initial"}}
        >
            <ClickAwayListener onClickAway={closePanel}>
                <CountrySelectorContainer data-testid={formatTextTestIds(`header-drawer-country-selector-container`)}>
                    <InnerContainer>
                        <Title variant="subtitle2">{headerTitle}</Title>
                        <StyledGrid container direction="row" justifyContent="center">
                            <CountrySelectorContainerElement>
                                <CountrySelectDropdown />
                            </CountrySelectorContainerElement>
                            {showLanguageSelector && (
                                <LanguageButtonsContainer data-testid="language-buttons-container">
                                    {(selectedCountry?.Languages?.length ?? 0) > 2 ? (
                                        <CountrySelectLanguageDropdown
                                            territory={territory}
                                            textAlignment={textAlignment}
                                            selectedCountry={selectedCountry}
                                            selectLanguage={selectLanguage}
                                            selectedLanguage={(selectedLanguage as string) ?? ""}
                                        />
                                    ) : (
                                        selectedCountry?.Languages.map((lan: CountryLanguage, i) => (
                                            <LanguageButton
                                                key={lan.AccountDomainUrl}
                                                selected={selectedLanguage === lan.Name}
                                                onClick={() => selectLanguage(lan.Name)}
                                                data-testid={`country-selector-language-button-${i}`}
                                                data-ga-v1={COUNTRY_LANG_SELECTOR_DATA_GA}
                                                data-ga-v2={selectedCountry.Country}
                                                data-ga-v3={lan.Value}
                                            >
                                                {lan.Value}
                                            </LanguageButton>
                                        ))
                                    )}
                                </LanguageButtonsContainer>
                            )}
                            <CountrySelectorCTAWrapper>
                                <CTA
                                    text={shopNowText}
                                    color="primary"
                                    state="enabled"
                                    variant="contained"
                                    size={isMedUp ? "medium" : "large"}
                                    onClick={onCTAClick}
                                    testId="country-selector-CTA-button"
                                />
                            </CountrySelectorCTAWrapper>
                        </StyledGrid>
                        {ROWText && (
                            <Grid item xs={12}>
                                <CountrySelectorROW>
                                    <CountrySelectorROWText variant="body1">{ROWText}</CountrySelectorROWText>
                                    <ViewAllLink data-testid="country-selector-ROWLink" href={ROWLinkUrl}>
                                        {ROWLinkText}
                                    </ViewAllLink>
                                </CountrySelectorROW>
                            </Grid>
                        )}
                    </InnerContainer>
                    <CloseButton disableRipple data-testid="country-selector-close-button" onClick={closePanel}>
                        <img src={urls.crossIconBlack} alt="Close Icon" />
                    </CloseButton>
                </CountrySelectorContainer>
            </ClickAwayListener>
        </DrawerContainer>
    )
}

TabCountryLangSelector.displayName = "TabCountryLangSelector"

export default TabCountryLangSelector
